import type { Member } from './domain';
import type { FlowPostResponse, MemberState } from './flowTypes';
import type { LegacyPostTypesUnion } from './recognitionPostTypes';
import type { RepliesResponse } from './repliesTypes';
import type { Cursor } from './response';

export type SearchIndexDocumentType =
  | 'url'
  | 'comment'
  | 'file'
  | 'flow'
  | 'member'
  | 'post'
  | 'response'
  | 'recognition'
  | 'task'
  | 'weblink'
  | 'collection'
  | 'tangoReward'
  | 'swag'
  | 'customReward';

export type AssemblyResponseKind =
  | 'EXTERNAL_ANONYMOUS'
  | 'INTERNAL'
  | 'INTERNAL_ANONYMOUS';

export type BaseSearchResult = {
  createdAt: string;
  id: string;
  type: string;
  score: number;
  updatedAt: string;

  // TODO: move card details to appropriate types & updated related references
  cardDetails?: (FlowPostResponse | undefined) & {
    post?: LegacyPostTypesUnion['post'];
    replySummary?: RepliesResponse;

    // These are collection card details
    isPinned?: boolean;
    isShared?: boolean;
  };
};

type AssemblyBaseSearchResult = {
  source: 'assembly';
} & BaseSearchResult;

export type FileSearchResult = {
  _meta: {
    blockId: string;
    entityId: string;
    flowDetails: {
      emoji: string;
      flowId: string;
      name: string;
    };
    name: string;
    originalName: string;
    responseDetails: {
      respondent: {
        fullName: string;
      };
    };
    responseId: string;
    type: string;
    uploadedAt: string;
  };
  highlight: {
    '_meta.name'?: string[];
    '_meta.flowDetails.name'?: string[];
    'deepSearchableContent.content'?: string[];
    '_meta.responseDetails.respondent.fullName'?: string[];
    '_meta.originalName'?: string[];
  };
  type: 'file';
} & AssemblyBaseSearchResult;

export type FlowSearchResult = {
  _meta: {
    description: string;
    entityId: string;
    icon: string;
    name: string;
    ownerId: string;
  };
  highlight: {
    '_meta.description'?: string[];
    '_meta.name'?: string[];
  };
  type: 'flow';
  state?: FlowStatusType;
} & AssemblyBaseSearchResult;

export type RecognitionSearchResult = {
  _meta: {
    description: string;
    entityId: string;
    icon: string;
    name: string;
  };
  highlight: {
    '_meta.description'?: string[];
    '_meta.name'?: string[];
  };
  type: 'recognition';
} & AssemblyBaseSearchResult;

export type MemberSearchResult = {
  _meta: {
    entityId: string;
    department?: string;
    email: string;
    fullName: string;
    jobTitle?: string;
    location?: string;
    profileImageUrl?: string;
    state?: MemberState;
  };
  highlight: {
    '_meta.department'?: string[];
    '_meta.email'?: string[];
    '_meta.fullName'?: string[];
    '_meta.jobTitle'?: string[];
    '_meta.location'?: string[];
  };
  type: 'member';
  state?: FlowStatusType;
} & AssemblyBaseSearchResult;

type CommentBaseMeta = {
  entityId: string;
  from: { fullName: string; id: string };
  message: string;
  gifURL?: string;
};

type CommentBaseSearchResult = {
  highlight: {
    '_meta.flowDetails.name'?: string[];
    '_meta.from.fullName'?: string[];
    '_meta.message'?: string[];
    '_meta.postDetails.from.fullName'?: string[];
    '_meta.postDetails.to.fullName'?: string[];
    '_meta.postDetails.type'?: string[];
    '_meta.responseDetails.from.fullName'?: string[];
  };
  type: 'comment';
} & AssemblyBaseSearchResult;

type PostDetails = {
  fromRef: { entityId: string };
  from: { fullName?: string; id: string };
  to: [{ fullName: string; id: string }];
  type: 'anniversary' | 'birthday' | 'recognition';
};

export type PostCommentSearchResult = {
  _meta: {
    kind: 'post';
    postDetails: PostDetails;
    postId: string;
  } & CommentBaseMeta;
} & CommentBaseSearchResult;

export type ResponseCommentSearchResult = {
  _meta: {
    kind: 'response';
    flowDetails: {
      icon: string;
      name: string;
    };
    flowId: string;
    responseDetails: {
      fromRef: { entityId: string };
      from: { fullName: string };
      kind: AssemblyResponseKind;
    };
    responseId: string;
  } & CommentBaseMeta;
} & CommentBaseSearchResult;

export type CommentSearchResult =
  | PostCommentSearchResult
  | ResponseCommentSearchResult;

export type PostSearchResult = {
  _meta: {
    entityId: string;
    message: string;
  } & PostDetails;
  highlight: {
    '_meta.from.fullName'?: string[];
    '_meta.message'?: string[];
    '_meta.to.fullName'?: string[];
    '_meta.type'?: string[];
  };
  type: 'post';
} & AssemblyBaseSearchResult;

export type ResponseSearchResult = {
  _meta: {
    entityId: string;
    flowId: string;
    icon: string;
    name: string;
    respondent: { fullName: string; id: string };
    responseBlocks: string[];
    responseKind: AssemblyResponseKind;
    canShareAsAnnouncement?: boolean;
    canEndAnnouncement?: boolean;
    canEditAnnouncement?: boolean;
  };
  highlight: {
    '_meta.name'?: string[];
    '_meta.respondent.fullName'?: string[];
    '_meta.responseBlocks'?: string[];
    searchableContent?: string[];
    deepSearchableContent?: {
      content: string[];
    };
  };
  type: 'response';
} & AssemblyBaseSearchResult;

export type TaskSearchResult = {
  _meta: {
    description: string;
    entityId: string;
    title: string;
    state: 'COMPLETED' | 'ARCHIVED' | 'ACTIVE';
    transactionId: string;
    assignedTo: {
      id: string;
    };
  };
  highlight: {
    '_meta.description'?: string[];
    '_meta.title'?: string[];
  };
  state: string;
  type: 'task';
} & AssemblyBaseSearchResult;

export type UrlSearchResult = {
  _meta: {
    url: string;
    pageTitle: string;
    pageDescription: string;
    type: UrlSearchType;
  };
  type: 'url';
} & BaseSearchResult;

export type UrlSearchType =
  | 'admin'
  | 'people'
  | 'rewards'
  | 'notebook'
  | 'templates'
  | 'knowledgecenter';

export type CollectionSearchResult = {
  _meta: {
    details: {
      description?: string;
      icon: string;
      name: string;
      color: string;
      isPinned: boolean;
    };
    entityId: string;
    createdByRef: {
      department: Member['profile']['department'];
      entityId: Member['memberId'];
      fullName: Member['profile']['fullName'];
      jobTitle: Member['profile']['jobTitle'];
      location: Member['profile']['location'];
      managerIds: Member['managerIds'];
      profileImageUrl: Member['image'];
      profileStatus: Member['status'];
      roles: Member['role'];
      username: Member['profile']['username'];
    } & Pick<Member, 'email' | 'firstName' | 'lastName' | 'state'>;
  };
  type: 'collection';
} & BaseSearchResult;

export type LinkSearchResult = {
  _meta: {
    entityId: string;
    details: {
      url: string;
      title?: string;
    };
  };
  highlight: {
    '_meta.details.title'?: string[];
    '_meta.details.url'?: string[];
  };
  type: 'weblink';
} & AssemblyBaseSearchResult;

export type Denomination = {
  faceValue: number;
  _id: string;
  utid: string;
  currencyCode: string;
};

export type TangoRewardSearchResult = {
  _meta: {
    cardDetails: {
      brandKey: string;
      brandName: string;
      disclaimer: string;
      description: string;
      shortDescription: string;
      terms: string;
      imageUrls: {
        '278w-326ppi': string;
      };
      status: string;
      items: [
        {
          maxValue: number;
          minValue: number;
          rewardType: string;
          rewardName: string;
          redemptionInstructions: string;
          currencyCode: string;
          valueType: 'FIXED_VALUE' | 'VARIABLE_VALUE';
          faceValue: number;
          countries: string[];
        },
      ];
      denominations?: Denomination[];
    };
  };
  type: 'tangoReward';
  tags: ('gift card' | 'donation')[];
  name: string;
  highlight: {
    searchableContent: string[];
    name: string[];
    '_meta.cardDetails.brandName': string[];
    '_meta.cardDetails.items.rewardName': string[];
  };
};

export type SwagRewardSearchResult = {
  _meta: {
    id: string;
    details: {
      image: string;
      active: boolean;
      enabled: boolean;
      points: number[];
      uid: string;
      name: string;
      desc: string;
    };
  };
  type: 'swag';
  name: string;
  highlight: {
    searchableContent: string[];
    name: string[];
    '_meta.details.name': string[];
    '_meta.details.desc': string[];
    content: string[];
  };
};

export type CustomRewardSearchResult = {
  _meta: {
    entityId: string;
    name: string;
    image?: {
      original: {
        relativeUrl: string;
      };
    };
    description: string;
    quantity: number;
    pointsToRedeem: number;
    isLimit: boolean;
  };
  type: 'customReward';
  name: string;
  highlight: {
    searchableContent: string[];
    name: string[];
    '_meta.name': string[];
  };
};

export type RewardSearchResult = (
  | TangoRewardSearchResult
  | SwagRewardSearchResult
  | CustomRewardSearchResult
) &
  AssemblyBaseSearchResult;

export type AssemblySearchResult =
  | CommentSearchResult
  | FileSearchResult
  | FlowSearchResult
  | LinkSearchResult
  | MemberSearchResult
  | PostSearchResult
  | ResponseSearchResult
  | TaskSearchResult
  | UrlSearchResult
  | RecognitionSearchResult
  | CollectionSearchResult
  | RewardSearchResult;

const documentSources = [
  'assembly',
  'box',
  'dropbox',
  'googleDrive',
  'oneDrive',
  'sharePoint',
] as const;

type assemblyDocumentSources = ['assembly'];

export type ExternalDocSources = Exclude<
  (typeof documentSources)[number],
  'assembly'
>;

export type ExternalFileSearchResult = {
  _meta: {
    entityId: string;
    integrationIcon: string;
    integrationName: string;
    name: string;
    remoteLocation: string;
    type: string;
  };
  highlight: {
    '_meta.name'?: string[];
    '_meta.integrationName'?: string[];
  };
  source: ExternalDocSources;
  type: 'file';
} & BaseSearchResult;

export type Metadata = {
  pagination: Cursor;
  aggregations: {
    type: SearchIndexDocumentType | 'all';
    count: number;
  }[];
};

export type AssemblySearchIndexResponse = {
  data: SearchIndexResult[];
  maxScore: number;
  total: number;
  metadata: Metadata;
};

export type SearchIndexResult = AssemblySearchResult | ExternalFileSearchResult;

export enum UserActivitySortType {
  AtoZ = 'atoz',
  Relevance = 'relevance',
  MostViewed = 'mostViewed',
  RecentlyViewed = 'recentlyViewed',
}

export type FlowStatusType = 'ACTIVE' | 'INACTIVE';

export type TangoFilter = {
  countries?: string[];
};

export type AssemblySearchIndexRequestBody = {
  cursor: Cursor;
  filters: {
    createdAt?: DateRange;
    source: typeof documentSources | assemblyDocumentSources;
    state?: FlowStatusType[];
    type?: SearchIndexDocumentType[];
    tags?: string[];
    'assembly.fromRef'?: string[];
    'assembly.entityIn'?: string[];
    'assembly.mentionedMemberIds'?: string[];
    'swag.categories'?: number[];
  };
  tangoFilter: TangoFilter;
  indexName: 'search-assembly-v3-std';
  searchTerm?: string;
  sortOptions?: Record<string, string>[];
  userActivitySortType?: UserActivitySortType | null;
  populateCardDetails?: boolean;
};

export type DateRange = { lte: string; gte: string };
